import React, { useEffect, useRef, useState } from 'react'
import { Checkbox } from '@material-ui/core'
import moment from 'moment'
import MultiCreateSection from './MultiCreateSection'

export const INTERVALS = {
  WEEK: {
    key: 'WEEK',
    plural: 'Weeks',
    label: 'Week',
  },
  MONTH: {
    key: 'MONTH',
    plural: 'Months',
    label: 'Month',
  },
}
export const WEEK_INTERVALS = {
  // EVERY: { //EVERY is = to week interval
  //   key: 'EVERY',
  //   label: 'Every',
  // },
  FIRST: {
    key: 'FIRST',
    label: 'The First',
    number: 0,
  },
  SECOND: {
    key: 'SECOND',
    label: 'The Second',
    number: 1,
  },
  THIRD: {
    key: 'THIRD',
    label: 'The Third',
    number: 2,
  },
  FOURTH: {
    key: 'FOURTH',
    label: 'The Fourth',
    number: 3,
  },
  LAST: {
    key: 'LAST',
    label: 'The Last',
    number: -1,
  },
}
export const WEEKDAYS = {
  SUNDAY: {
    value: 'SUNDAY',
    label: 'Sunday',
    number: moment().isoWeekday('Sunday').isoWeekday(),
  },
  MONDAY: {
    value: 'MONDAY',
    label: 'Monday',
    number: moment().isoWeekday('Monday').isoWeekday(),
  },
  TUESDAY: {
    value: 'TUESDAY',
    label: 'Tuesday',
    number: moment().isoWeekday('Tuesday').isoWeekday(),
  },
  WEDNESDAY: {
    value: 'WEDNESDAY',
    label: 'Wednesday',
    number: moment().isoWeekday('Wednesday').isoWeekday(),
  },
  THURSDAY: {
    value: 'THURSDAY',
    label: 'Thursday',
    number: moment().isoWeekday('Thursday').isoWeekday(),
  },
  FRIDAY: {
    value: 'FRIDAY',
    label: 'Friday',
    number: moment().isoWeekday('Friday').isoWeekday(),
  },
  SATURDAY: {
    value: 'SATURDAY',
    label: 'Saturday',
    number: moment().isoWeekday('Saturday').isoWeekday(),
  },
}
export const getDefaultSchedule = (defaults) => ({
  every: 1,
  interval: INTERVALS.WEEK.key,
  on: [WEEKDAYS.MONDAY],
  until: moment(new Date()).add(1, 'month'),
  weekInterval: WEEK_INTERVALS.FIRST,
  ...defaults,
})

const getScheduledEventSet = (schedules, edits) => {
  let scheduledEvents = schedules
    .map((schedule) => getEventsFromSchedule(edits, schedule))
    .flat(1)
  const foundDates = {}
  scheduledEvents = scheduledEvents.filter((event) => {
    if (!foundDates[event.startDateTime.toDate()]) {
      foundDates[event.startDateTime.toDate()] = true
      return true
    }
  })
  return scheduledEvents
}

const Scheduling = (props) => {
  const { edits, handleChange } = props
  const event = props.event.toJS()
  const [createMultiple, setCreateMultiple] = useState(false)
  const [schedules, setSchedules] = useState([])
  const [scheduledEventProps, setScheduledEventProps] = useState({})

  /*
    Both effects below fire once on mount with empty defaults, which the parent
    wizard cannot tell apart from a user edit -- it treats every handleChange as
    "the user fixed something" and clears the validation errors. If this step is
    ever remounted while an error modal is open, that mount-time sync would wipe
    the modal's contents. Skip the first pass; there is nothing to report yet.
  */
  const hasMounted = useRef(false)

  useEffect(() => {
    if (!hasMounted.current) return
    // merge schedules and remove duplicate events by startDateTime
    const scheduledEvents = getScheduledEventSet(schedules, edits)
    edits.schedules = scheduledEvents

    // add/remove events that don't have a matching date
    const newScheduledEventProps = {}
    scheduledEvents.forEach((event) => {
      newScheduledEventProps[event.startDateTime] = {
        startDateTime: event.startDateTime,
        ...scheduledEventProps[event.startDateTime],
      }
    })
    setScheduledEventProps(newScheduledEventProps)
    handleChange('schedules', edits.schedules)
  }, [schedules])

  useEffect(() => {
    if (!hasMounted.current) {
      // runs after the effect above, so both are skipped on the first pass
      hasMounted.current = true
      return
    }
    handleChange('scheduledEventProps', scheduledEventProps)
  }, [scheduledEventProps])

  const handleChangeSchedule = (index, value) => {
    const newSchedules = [...schedules]
    newSchedules[index] = value
    setSchedules(newSchedules)
  }

  const handleAddSchedule = (newSchedule) => {
    setSchedules([...schedules, newSchedule])
  }

  const handleRemoveSchedule = (index) => {
    const newSchedules = [...schedules]
    newSchedules.splice(index, 1)
    setSchedules(newSchedules)
  }

  const handleSetCreateMultiple = (ev) => {
    const enabled = ev.target.checked
    setCreateMultiple(enabled)
    if (enabled && !schedules.length) {
      setSchedules([getDefaultSchedule({ until: edits.startDateTime })])
    }
  }

  return (
    <div className='callout'>
      <Checkbox
        checked={createMultiple}
        color='primary'
        onChange={handleSetCreateMultiple}
      />
      Create multiple events based on this event
      {createMultiple ? (
        <MultiCreateSection
          event={event}
          edits={edits}
          schedules={schedules}
          onChange={handleChangeSchedule}
          onAdd={handleAddSchedule}
          onRemove={handleRemoveSchedule}
          scheduledEventProps={scheduledEventProps}
          setScheduledEventProps={setScheduledEventProps}
        />
      ) : null}
    </div>
  )
}

/**
weekday ex: {
  value: 'MONDAY', label: 'Monday'
}
schedule ex:{
  every: #,
  interval: 'WEEK' / 'MONTH',
  weekInterval: 'FIRST' / 'SECOND' / 'THIRD' / 'FOURTH' / 'LAST', //used only with month interval
  on: [
    weekdays...
  ],
  until: momentDate
}
 */

export const getEventsFromSchedule = (edits, schedule) => {
  let scheduledEvents = []
  const scheduleStart = moment(edits.startDate)
  const scheduleEnd = moment(schedule.until)

  const onWeekday = schedule.on.map((weekday) => weekday.number)

  if (schedule.interval === INTERVALS.WEEK.key) {
    scheduledEvents = getWeekScheduledEvents({scheduleStart, scheduleEnd, onWeekday, schedule})
  } else if (schedule.interval === INTERVALS.MONTH.key) {
    // from the start of the month check the weeks in shedule.weekInterval for the days selected
    scheduledEvents = getMonthScheduledEvents({scheduleStart, scheduleEnd, onWeekday, schedule})
  } else {
    console.error('bad schedule interval:', schedule.interval)
  }
  return scheduledEvents
}

export default Scheduling
function getMonthScheduledEvents({scheduleStart, scheduleEnd, onWeekday, schedule}) {
  let curDate = scheduleStart.clone().startOf('month')
  let scheduledEvents = []
  const getZeroedMonthDays = () => ({
    [WEEKDAYS.MONDAY.number]: 0,
    [WEEKDAYS.TUESDAY.number]: 0,
    [WEEKDAYS.WEDNESDAY.number]: 0,
    [WEEKDAYS.THURSDAY.number]: 0,
    [WEEKDAYS.FRIDAY.number]: 0,
    [WEEKDAYS.SATURDAY.number]: 0,
    [WEEKDAYS.SUNDAY.number]: 0,
  })

  let monthDays = getZeroedMonthDays()

  console.log('getMonthScheduledEvents', scheduleStart.format('DD.MM.YY'));
  console.log('getMonthScheduledEvents.curDate', curDate.format('DD.MM.YY'));
  while (curDate.isSameOrBefore(scheduleEnd)) {
    if (onWeekday.includes(curDate.isoWeekday())) {
      //schedule events on this day of the week, check if this number of that day for the week
      // matching everything based on the number given by moment for months and # of times this day has been encountered this month
      // LAST (number === -1) matches the final occurrence of this weekday in the month:
      // adding a week lands in the next month, so no later same-weekday exists this month.
      const matchesInterval =
        schedule.weekInterval.number === WEEK_INTERVALS.LAST.number
          ? curDate.clone().add(7, 'days').month() !== curDate.month()
          : monthDays[curDate.isoWeekday()] === schedule.weekInterval.number
      if (matchesInterval) {
        // Inclusive of the "Until" date (exclusive of the source event's own
        // start date) so a last-occurrence landing exactly on Until is kept,
        // matching the week path's isSameOrBefore(scheduleEnd) behavior.
        if (curDate.isBetween(scheduleStart, scheduleEnd, undefined, '(]'))
          scheduledEvents.push({ startDateTime: curDate.clone() })
      }
    }
    monthDays[curDate.isoWeekday()] += 1

    // reset month weekday counter when the next day is the next month
    if (curDate.clone().add(1, 'day').month() !== curDate.month()) {
      monthDays = getZeroedMonthDays()
    }
    curDate.add(1, 'day')
  }
  return scheduledEvents
}

function getWeekScheduledEvents({scheduleStart, scheduleEnd, onWeekday, schedule}) {
  let scheduledEvents = []
  let skipWeeks = 0

  console.log('getWeekScheduledEvents', scheduleStart.format('DD.MM.YY'));
  
  for (let curDate = scheduleStart.clone().add(1, 'day'); curDate.isSameOrBefore(scheduleEnd); curDate.add(1, 'day')) {
    if (skipWeeks <= 0 && onWeekday.includes(curDate.isoWeekday())) {
      scheduledEvents.push({ startDateTime: curDate.clone() })
    }
    if (curDate.isoWeekday() === scheduleStart.isoWeekday()) {
      if (skipWeeks > 0) {
        skipWeeks -= 1
      } else {
        skipWeeks = schedule.every - 1
      }
    }
  }
  return scheduledEvents
}

